import React, { useCallback, useEffect, useRef, useState } from 'react';
import './LoiSubmissionPackage.scss';
import { useSearchParams } from 'react-router-dom';
import { TermForm } from '../../../../../slices/marketplace/loi-form/term-form';
import { LoiAndTermsForm } from './LoiAndTermsForm/LoiAndTermsForm';
import { LegalTermGuidance } from './LegalTermGuidance/LegalTermGuidance';
import { AdditionalNdaCovenants } from './AdditionalNdaCovenants/AdditionalNdaCovenants';
import { useStateSelector } from '../../../../../store/selectors';
import createNotification from '../../../../../utils/createNotification';
import ButtonLoader from '../../../../../components/Layout/Buttons/ButtonLoader';
import InfoTooltip from '../../../../../components/info-tooltip/info-tooltip';
import { fetchLoiForm, updateLoiForm, submitLoiForm, submitAdminLoiForms, confirmLoiReview, saveLoiFormRevision } from '../../../../../slices/marketplace/loi-form/loi-forms-api';
import { effectiveLeaseAssumption } from '../../../../../slices/marketplace/loi-form/effective-lease-assumption';
import { approveLoiOnBehalfOfBuyer, completeAdminLoiResubmission, confirmBuyerLoiResubmission, submitBuyerLoiResubmission } from '../../../../../slices/marketplace/loi-comparison/loi-comparison-api';
import { LoiForm } from '../../../../../slices/marketplace/loi-form/loi-form';
import { LegalIssueItem } from '../../../../../slices/marketplace/loi-form/legal-issue-item';
import { LoiFormSection } from '../../../../../slices/marketplace/loi-form/loi-form-section';
import { LoiFormErrors, validateLoiForm, hasLoiFormErrors } from '../../../../../slices/marketplace/loi-form/loi-form-validator';
import { LoiFormsProcess, LoiFormsProcessStep, LoiFormsProcessStepStatus } from '../../../../../slices/marketplace/loi-comparison/models/loi-forms-process.model';
import {
    PendingFieldChange,
    PendingFieldChangeKey,
    ScopedBaseline,
    applyBaselineValueToTermForm,
    applyPendingChangeToTermForm,
    buildScopedBaseline,
    deriveAdminScopedPendingChanges,
} from '../../../../../slices/marketplace/loi-form/pending-field-change';
import { GeneralDeferredPaymentsFieldKeys } from '../../../../../slices/marketplace/loi-form/loi-term-form-field-keys';
import { resolveInitialQuestionsAccess } from '../../../../../slices/marketplace/loi-form/loi-initial-questions-access';
import { resolveLegalTabsReadonly } from '../../../../../slices/marketplace/loi-form/loi-legal-tabs-access';
import { ADDITIONAL_NDA_COVENANTS_BUYER_TOOLTIP } from '../../../../../slices/marketplace/loi-form/loi-form-tooltips';
import ConfirmationModal from '../../../../../components/Modal/ConfirmationModal';
import Helpers from '../../../../../utils/helper';
import { useDispatch } from 'react-redux';
import { addNotificationFlagToDelete } from '../../../../../slices/notification-flags/notification-flags.slice';
import { NotificationFlagsCategories } from '../../../../../models/NotificationFlags/NotificationFlagsCategories';
import { ToastContainer, Slide } from 'react-toastify';
import { useLoiFormAutoSave, AutoSaveSection, AUTO_SAVE_CONTAINER_ID } from './useLoiFormAutoSave';
import { useVendorContractsInit } from './useVendorContractsInit';
import { useOwnerEmploymentAgreementDurationInit } from './useOwnerEmploymentAgreementDurationInit';

import '../../../../../components/AutoSaveToast/AutoSaveToast.scss'

export enum LoiSubmissionPackageSubTab {
    LoiAndTermsForm = 1,
    LegalTermGuidance = 2,
    AdditionalNdaCovenants = 3,
    OwnerObligations = 4,
}

const LoiSubmissionPackageSubTabs = [
    {
        id: LoiSubmissionPackageSubTab.LoiAndTermsForm,
        content: 'LOI',
    },
    {
        id: LoiSubmissionPackageSubTab.LegalTermGuidance,
        content: 'Legal Terms Guidance',
    },
    {
        id: LoiSubmissionPackageSubTab.AdditionalNdaCovenants,
        content: 'Additional NDA Covenants',
    },
];

const loiReviewSubmitConfirmationText = (hasBlankFields: boolean) => (
    <span>
        Are you sure you're ready to submit your comments on the LOI Form and Legal Issues
        Guidance?
        {hasBlankFields && (
            <> Several fields are still blank. Completing them can provide greater transparency
            and help the client evaluate your offer with confidence.</>
        )}
    </span>
);

const LOI_PRELIMINARY_SUBMIT_CONFIRMATION_TEXT = (
    <span>
        VetValue will review your submission and respond within 24 hours with a detailed form.
        You'll have the opportunity to comment and provide legal terms before your offer is sent to
        the seller.
    </span>
);

const LOI_SUBMIT_CONFIRMATION_TEXT = (
    <span>
       By submitting the detailed LOI form, you confirm that all information from the uploaded LOI document has been accurately captured.
Once submitted, the buyer will have an opportunity to review and provide comments before all LOIs and detailed forms are shared with sellers.
    </span>
);

const LOI_APPROVE_ON_BEHALF_CONFIRMATION_TEXT = (
    <span>
        This saves the form and confirms it for the buyer, skipping their review step. Any changes
        to the preliminary questions still awaiting the buyer's approval become the values on this
        version. If the seller's LOI comparison is already in progress, the version is posted to
        the seller immediately; otherwise it is posted when the comparison starts.
    </span>
);

const LOI_REVIEW_CONFIRMATION_TEXT = (
    <span>
        By pressing <strong>Confirm</strong>, you accept the LOI form as presented. If you do not
        confirm before the deadline, the LOI will be accepted automatically.
    </span>
);

interface LoiSubmissionPackageProps {
    loiFormId?: string;
    isReadonly: boolean;
    isReadonlyDueToPermissions?: boolean;
    isBuyerInactive?: boolean;
    setSearchParams?: (params: { [key: string]: string }) => void;
    engagementId: number;
    eventId?: string;
    selectedProcess?: LoiFormsProcess;
    documentDetailsId?: string;
    uploadedDocumentName?: string;
    onDataChange?: () => void;
    isBuyerReviewConfirmed?: boolean;
    isFsb?: boolean;
    isSfas?: boolean;
    isAdmin?: boolean;
    isBuyer?: boolean;
    canSubmit: boolean;
    isResubmission?: boolean;
    buyerId?: number;
    isHistoricalVersion?: boolean;
    isLoiSelectionFinalized?: boolean;
    isLoiCreationEventInProgress?: boolean;
    isLoiTabTermsOnly?: boolean;
    showAdditionalNdaCovenantsTooltip?: boolean;
    showOwnerObligationsTab?: boolean;
    isOwnerObligationsTabOnly?: boolean;
}

function findChangedLegalIssueSectionIds(prev: LegalIssueItem[], next: LegalIssueItem[]): string[] {
    const prevMap = new Map(prev.map(item => [item.id, item]));
    const changedSections = new Set<string>();
    for (const item of next) {
        const prevItem = prevMap.get(item.id);
        if (!prevItem || prevItem.value !== item.value) {
            if (item.sectionId) changedSections.add(item.sectionId);
        }
    }
    return Array.from(changedSections);
}

export const LoiSubmissionPackage = (props: LoiSubmissionPackageProps) => {
    const [searchParams] = useSearchParams({});
    const [activeSubTab, setActiveSubTab] = useState<LoiSubmissionPackageSubTab>(
        props.showOwnerObligationsTab
            ? LoiSubmissionPackageSubTab.OwnerObligations
            : LoiSubmissionPackageSubTab.LoiAndTermsForm
    );
    const [formData, setFormData] = useState<LoiForm | null>(null);
    const formDataRef = useRef<LoiForm | null>(null);
    useEffect(() => { formDataRef.current = formData; }, [formData]);
    const scopedBaselineRef = useRef<ScopedBaseline>(buildScopedBaseline(undefined));
    const revisionBaselineRef = useRef<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<LoiFormErrors | null>(null);
    const [isConfirmVisible, setIsConfirmVisible] = useState(false);
    const [hasBlankFieldsOnSubmit, setHasBlankFieldsOnSubmit] = useState(false);
    const [isApproveOnBehalfConfirmVisible, setIsApproveOnBehalfConfirmVisible] = useState(false);
    const [isDocumentUploadedLocally, setIsDocumentUploadedLocally] = useState(false);
    const axios = useStateSelector((state) => state.core.axios);
    const dispatch = useDispatch();
    const isBuyerLoiSubmission = !props.isHistoricalVersion && props.selectedProcess?.step === LoiFormsProcessStep.BuyerSubmission;
    const isBuyerLoiFormReview = !props.isHistoricalVersion
        && !props.isBuyerInactive
        && props.selectedProcess?.step === LoiFormsProcessStep.BuyerReview
        && props.selectedProcess?.stepStatus === LoiFormsProcessStepStatus.InProgress;
    const isProcessCompleted = props.selectedProcess?.step === LoiFormsProcessStep.BuyerReview && props.selectedProcess?.stepStatus === LoiFormsProcessStepStatus.Completed;
    const isPresentedVersion = !!props.selectedProcess?.isPostedToSeller;
    const isAdminRevisionEdit = !!props.isAdmin && !props.isHistoricalVersion && !props.isReadonly && isPresentedVersion;
    const isAdminInPlaceReviewEdit = !!props.isAdmin && !props.isHistoricalVersion && !props.isReadonly && isProcessCompleted && !props.selectedProcess?.isPostedToSeller;
    const isAdminBuyerReviewEdit = (!!props.isAdmin && !props.isHistoricalVersion && !props.isReadonly && isBuyerLoiFormReview) || isAdminInPlaceReviewEdit;
    const isAdminApproveOnBehalfAvailable = !!props.isAdmin && props.buyerId != null
        && !props.isHistoricalVersion && !props.isReadonly && !props.isBuyerInactive
        && !props.isLoiSelectionFinalized
        && (props.selectedProcess?.step === LoiFormsProcessStep.AdminReview || isBuyerLoiFormReview);
    const effectiveBuyerReviewConfirmed = !!props.isBuyerReviewConfirmed || isProcessCompleted;
    const isInitialSubmission = isBuyerLoiSubmission && !props.isAdmin;
    const isBuyerAwaitingDetailedForm = !!props.isBuyer && !props.isHistoricalVersion && !props.isLoiTabTermsOnly
        && (props.selectedProcess?.step === LoiFormsProcessStep.BuyerSubmission
            || props.selectedProcess?.step === LoiFormsProcessStep.AdminReview);
    const isFormReadonly = props.isReadonly || ((isBuyerLoiFormReview || isAdminInPlaceReviewEdit || isAdminRevisionEdit) ? !props.isAdmin : formData?.isComplete === true);
    const initialQuestionsAccess = resolveInitialQuestionsAccess({
        isAdmin: !!props.isAdmin,
        isBuyer: !!props.isBuyer,
        isHistoricalVersion: !!props.isHistoricalVersion,
        isReadonly: props.isReadonly,
        isReadonlyDueToPermissions: !!props.isReadonlyDueToPermissions,
        isBuyerInactive: !!props.isBuyerInactive,
        isPostedToSeller: !!props.selectedProcess?.isPostedToSeller,
        isBuyerReviewConfirmed: !!props.isBuyerReviewConfirmed,
        processStep: props.selectedProcess?.step,
        processStepStatus: props.selectedProcess?.stepStatus,
    });
    const preliminaryQuestionsReadonly = initialQuestionsAccess.isReadonly;
    const isLegalTabsReadonly = resolveLegalTabsReadonly({
        isAdmin: !!props.isAdmin,
        isReadonly: props.isReadonly,
        isHistoricalVersion: !!props.isHistoricalVersion,
        isBuyerInactive: !!props.isBuyerInactive,
        processStep: props.selectedProcess?.step,
        isInitialSubmission,
        isBuyerLoiFormReview,
        isBuyerReviewConfirmed: !!props.isBuyerReviewConfirmed,
        isFormComplete: formData?.isComplete === true,
    });
    const canApprovePendingChanges = initialQuestionsAccess.canApprovePendingChanges;
    const isAdditionalNdaReadonly = isInitialSubmission ? isFormReadonly : isLegalTabsReadonly;

    useEffect(() => {
        setIsDocumentUploadedLocally(false);
    }, [props.loiFormId]);

    useEffect(() => {
        const eventCode = `${NotificationFlagsCategories.MpCode}.${NotificationFlagsCategories.OpportunitiesCode}.${props.engagementId}.${NotificationFlagsCategories.LoiFormsCode}`;
        dispatch(addNotificationFlagToDelete({ eventCode, count: 0 }));
    }, [props.engagementId]);
    const isAdminAutoSaveBlocked = props.isBuyerInactive || props.isHistoricalVersion
        || (!!props.isAdmin && (isBuyerLoiFormReview || isProcessCompleted || isAdminRevisionEdit));
    const { markDirty, scheduleAutoSave, cancelPending, clearStatus: clearAutoSaveStatus } = useLoiFormAutoSave({
        loiFormId: props.loiFormId,
        axios,
        formData,
        isEnabled: !isAdminAutoSaveBlocked && !props.isReadonly,
    });

    const handleVendorContractTermFormChange = useCallback((termForm: TermForm) => {
        setFormData(prev => {
            if (!prev) return null;
            const prevTermForm = prev.termForm;
            return {
                ...prev,
                termForm: prevTermForm
                    ? { ...prevTermForm, vendorContractRows: termForm.vendorContractRows }
                    : termForm,
            };
        });
        markDirty(['termForm']);
        scheduleAutoSave();
    }, [markDirty, scheduleAutoSave]);

    const handleSellingOwnerEmploymentAgreementTermFormChange = useCallback((termForm: TermForm) => {
        setFormData(prev => {
            if (!prev) return null;
            const prevTermForm = prev.termForm;
            return {
                ...prev,
                termForm: prevTermForm
                    ? { ...prevTermForm, sellingOwnerEmploymentAgreementRows: termForm.sellingOwnerEmploymentAgreementRows }
                    : termForm,
            };
        });
        markDirty(['termForm']);
        scheduleAutoSave();
    }, [markDirty, scheduleAutoSave]);

    const {
        hasVendorContracts,
        vendorContractColumns,
        vendorContractRowDisplayData,
        vendorContractTableRows,
        vendorContractProjectId,
        vendorContractTableId,
        vendorContractFileColumnName,
        vendorContractFileColumnId,
    } = useVendorContractsInit({
        engagementId: props.engagementId,
        termForm: formData?.termForm,
        onChange: handleVendorContractTermFormChange,
        axios,
    });

    const { hasOwnerEmploymentAgreement, columns: ownerEmploymentColumns, rowDisplayData: ownerEmploymentRowDisplayData, tableRows: ownerEmploymentTableRows } = useOwnerEmploymentAgreementDurationInit({
        engagementId: props.engagementId,
        termForm: formData?.termForm,
        onChange: handleSellingOwnerEmploymentAgreementTermFormChange,
        axios,
    });

    useEffect(() => {
        if (props.isReadonly) return;
        const subtabValue = searchParams.get('subtab');
        const numberValue = Number(subtabValue);
        if (
            subtabValue &&
            numberValue &&
            numberValue >= 1 &&
            numberValue <= LoiSubmissionPackageSubTab.AdditionalNdaCovenants &&
            !((isInitialSubmission || isBuyerAwaitingDetailedForm) && numberValue === LoiSubmissionPackageSubTab.LegalTermGuidance)
        ) {
            setActiveSubTab(numberValue);
        }
    }, [searchParams, isInitialSubmission, isBuyerAwaitingDetailedForm]);

    const loadForm = useCallback(async () => {
        if (!axios || !props.loiFormId) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        setErrors(null);
        try {
            const data = await fetchLoiForm(axios, props.loiFormId);
            scopedBaselineRef.current = buildScopedBaseline(data.termForm);
            let effectiveData = data;
            if (props.isAdmin && data.termForm) {
                const bakedTermForm = (data.pendingFieldChanges ?? []).reduce(
                    (termForm, change) => applyPendingChangeToTermForm(termForm, change),
                    data.termForm
                );
                effectiveData = { ...data, termForm: bakedTermForm };
            }
            setFormData(effectiveData);
            revisionBaselineRef.current = JSON.stringify(effectiveData);
        } catch {
            createNotification('Could not load LOI form. Please try again.', 'error');
        } finally {
            setIsLoading(false);
        }
    }, [axios, props.loiFormId, props.isAdmin]);

    useEffect(() => {
        loadForm();
    }, [loadForm]);

    const documentDetailsId = props.documentDetailsId;
    const validationDocumentDetailsId = props.documentDetailsId ?? (isDocumentUploadedLocally ? 'uploaded-locally' : undefined);

    const buildStep1Errors = useCallback((updated: LoiForm, currentDocumentDetailsId: string | undefined): LoiFormErrors => {
        const termFormFields = new Set<string>();
        if (hasOwnerEmploymentAgreement === true) {
            const savedRows = updated.termForm?.sellingOwnerEmploymentAgreementRows ?? [];
            // Every owner must answer Yes/No explicitly; a Yes answer also requires the employment term.
            const hasUnansweredOwner = ownerEmploymentTableRows.some(
                tr => !savedRows.find(r => r.tableRowId === tr.tableRowId)
            );
            const hasEmployedWithoutTerm = savedRows.some(
                r => (r.isEmployedPostClose ?? true) && r.durationMonths === null
            );
            if (hasUnansweredOwner || hasEmployedWithoutTerm) termFormFields.add('sellingOwnerEmploymentAgreementRows');
        }
        if (hasVendorContracts === true) {
            // Every seller obligation needs an explicit Assume / Payoff at Close answer.
            const vendorRows = updated.termForm?.vendorContractRows ?? [];
            const hasUnansweredObligation = vendorContractTableRows.some(
                tr => !vendorRows.find(r => r.tableRowId === tr.tableRowId)
            );
            if (hasUnansweredObligation) termFormFields.add('vendorContractRows');
        }
        const sellerLocations = updated.sellerLocations ?? [];
        if (sellerLocations.length > 0 && sellerLocations.some(loc => !effectiveLeaseAssumption((updated.termForm?.locations ?? []).find(l => l.locationId === loc.locationId)?.leaseAssumption, loc))) termFormFields.add('locations');
        if (!updated.termForm?.isLegalInfoConfirmed) termFormFields.add('isLegalInfoConfirmed');
        return {
            termFormFields,
            legalIssueIds: new Set<string>(),
            additionalCovenantIds: new Set<string>(),
            loiFileRequired: !currentDocumentDetailsId,
        };
    }, [hasOwnerEmploymentAgreement, ownerEmploymentTableRows, hasVendorContracts, vendorContractTableRows]);

    const revalidate = useCallback((updated: LoiForm) => {
        setErrors(prev => {
            if (!prev) return null;
            return isBuyerLoiSubmission
                ? buildStep1Errors(updated, validationDocumentDetailsId)
                : validateLoiForm(updated, { documentDetailsId: validationDocumentDetailsId, isFsb: props.isFsb, skipLegalTabs: props.isAdmin, isAdmin: props.isAdmin });
        });
    }, [validationDocumentDetailsId, buildStep1Errors, isBuyerLoiSubmission, props.isFsb, props.isAdmin]);

    const handleTermFormChange = useCallback((incomingTermForm: TermForm) => {
        const hasChanged = JSON.stringify(formDataRef.current?.termForm) !== JSON.stringify(incomingTermForm);
        setFormData(prev => {
            if (!prev) return null;
            let updated: LoiForm;
            if (props.isAdmin && !isAdminRevisionEdit) {
                const pendingFieldChanges = deriveAdminScopedPendingChanges(incomingTermForm, scopedBaselineRef.current);
                updated = { ...prev, termForm: incomingTermForm, pendingFieldChanges };
            } else {
                updated = { ...prev, termForm: incomingTermForm };
            }
            revalidate(updated);
            return updated;
        });
        if (hasChanged) {
            markDirty(['termForm']);
            scheduleAutoSave();
        }
    }, [revalidate, markDirty, scheduleAutoSave, props.isAdmin, isAdminRevisionEdit]);

    const handleDiscardPendingChange = useCallback(async (fieldKey: PendingFieldChangeKey, rowKey: string | null) => {
        if (rowKey == null) return;
        const baseline = scopedBaselineRef.current;
        setFormData(prev => {
            if (!prev || !prev.termForm) return prev;
            const termForm = applyBaselineValueToTermForm(prev.termForm, fieldKey, rowKey, baseline);
            const pendingFieldChanges = deriveAdminScopedPendingChanges(termForm, baseline);
            const updated = { ...prev, termForm, pendingFieldChanges };
            revalidate(updated);
            return updated;
        });
        markDirty(['termForm']);
        scheduleAutoSave();
    }, [revalidate, markDirty, scheduleAutoSave]);

    const handleLegalIssuesChange = useCallback((legalIssues: LegalIssueItem[], legalIssueSections: LoiFormSection[]) => {
        const changedSectionIds = findChangedLegalIssueSectionIds(formData?.legalIssues ?? [], legalIssues);
        setFormData(prev => {
            const updated = prev ? { ...prev, legalIssues, legalIssueSections } : null;
            if (updated) revalidate(updated);
            return updated;
        });
        if (changedSectionIds.length > 0) {
            markDirty(changedSectionIds.map(id => `legalIssues:${id}` as AutoSaveSection));
        }
    }, [revalidate, markDirty, formData?.legalIssues]);

    const handleAdditionalCovenantsChange = useCallback((additionalCovenants: LegalIssueItem[]) => {
        setFormData(prev => {
            const updated = prev ? { ...prev, additionalCovenants } : null;
            if (updated) revalidate(updated);
            return updated;
        });
        markDirty(['additionalCovenants']);
    }, [revalidate, markDirty]);

    const handleFieldCommentChange = useCallback((fieldKey: string, buyerComment: string | null) => {
        setFormData(prev => {
            if (!prev) return prev;
            const existing = prev.termForm?.fieldComments ?? [];
            const updated = existing.some(c => c.fieldKey === fieldKey)
                ? existing.map(c => c.fieldKey === fieldKey ? { ...c, buyerComment } : c)
                : buyerComment !== null
                  ? [...existing, { fieldKey, buyerComment }]
                  : existing;
            const updatedForm = {
                ...prev,
                termForm: prev.termForm ? { ...prev.termForm, fieldComments: updated } : prev.termForm,
            };
            // A buyer comment can waive required obligor fields, so errors must react to it.
            revalidate(updatedForm);
            return updatedForm;
        });
        markDirty(['termForm']);
    }, [revalidate, markDirty]);

    const handleAdminCommentChange = useCallback((fieldKey: string, adminComment: string | null) => {
        setFormData(prev => {
            if (!prev) return prev;
            const existing = prev.termForm?.fieldComments ?? [];
            const updated = existing.some(c => c.fieldKey === fieldKey)
                ? existing.map(c => c.fieldKey === fieldKey ? { ...c, adminComment } : c)
                : adminComment !== null
                  ? [...existing, { fieldKey, buyerComment: null, adminComment }]
                  : existing;
            return {
                ...prev,
                termForm: prev.termForm ? { ...prev.termForm, fieldComments: updated } : prev.termForm,
            };
        });
        markDirty(['termForm']);
    }, [markDirty]);

    const handleSave = useCallback(async (): Promise<boolean> => {
        if (!axios || !props.loiFormId || !formData) return false;
        cancelPending();
        setIsSaving(true);
        try {
            if (isAdminRevisionEdit) {
                const currentFormSnapshot = JSON.stringify(formData);
                if (currentFormSnapshot === revisionBaselineRef.current) {
                    createNotification('No changes to save.', 'info');
                    return true;
                }
                await saveLoiFormRevision(
                    axios, props.engagementId, props.loiFormId, formData);
                revisionBaselineRef.current = currentFormSnapshot;
                clearAutoSaveStatus();
                createNotification('LOI form has been saved successfully.', 'success');
                return true;
            }
            await updateLoiForm(axios, props.loiFormId, {
                ...formData,
                id: props.loiFormId,
                isAdminTemplate: false,
            });
            clearAutoSaveStatus();
            createNotification('LOI form has been saved successfully.', 'success');
            return true;
        } catch {
            createNotification('Could not save LOI form. Please try again.', 'error');
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [axios, props.loiFormId, props.engagementId, formData, cancelPending, isAdminRevisionEdit, clearAutoSaveStatus]);

    const handleSubmitClick = useCallback(() => {
        if (!formData) return;

        const validationErrors = isBuyerLoiSubmission
            ? buildStep1Errors(formData, validationDocumentDetailsId)
            : validateLoiForm(formData, { documentDetailsId: validationDocumentDetailsId, isFsb: props.isFsb, skipLegalTabs: props.isAdmin, isAdmin: props.isAdmin });
        const hasErrors = isBuyerLoiSubmission
            ? validationErrors.termFormFields.size > 0 || !!validationErrors.loiFileRequired
            : hasLoiFormErrors(validationErrors);
        if (hasErrors) {
            setErrors(validationErrors);
            createNotification('Please fill all required fields and upload the LOI file before submitting.', 'error');
            return;
        }

        setErrors(null);
        setIsConfirmVisible(true);
    }, [formData, validationDocumentDetailsId, buildStep1Errors, isBuyerLoiSubmission, props.isFsb]);

    const handleConfirmLoiClick = useCallback(() => {
        if (!formData) return;

        const fullErrors = validateLoiForm(formData, { documentDetailsId: validationDocumentDetailsId, isFsb: props.isFsb, isAdmin: false });
        const buyerErrors: LoiFormErrors = {
            termFormFields: new Set([
                ...GeneralDeferredPaymentsFieldKeys.filter((key) => fullErrors.termFormFields.has(key)),
                ...Array.from(fullErrors.termFormFields).filter((key) => key.startsWith('sellerCovenant:')),
            ]),
            legalIssueIds: fullErrors.legalIssueIds,
            additionalCovenantIds: fullErrors.additionalCovenantIds,
        };
        const hasBuyerErrors = buyerErrors.termFormFields.size > 0 || buyerErrors.legalIssueIds.size > 0 || buyerErrors.additionalCovenantIds.size > 0;

        setErrors(hasBuyerErrors ? buyerErrors : null);
        setHasBlankFieldsOnSubmit(hasBuyerErrors);
        setIsConfirmVisible(true);
    }, [formData, validationDocumentDetailsId, props.isFsb]);

    const handleSubmitConfirm = useCallback(async () => {
        if (!axios || !props.loiFormId || !props.engagementId || !formData) return;

        setIsSubmitting(true);
        try {
            const saved = await handleSave();
            if (!saved) return;

            if (isBuyerLoiFormReview) {
                if (props.isResubmission && props.buyerId != null) {
                    await confirmBuyerLoiResubmission(axios, props.engagementId, props.buyerId);
                } else if (props.eventId) {
                    await confirmLoiReview(axios, props.engagementId, props.eventId);
                }
            } else if (isBuyerLoiSubmission) {
                if (props.isResubmission && props.buyerId != null) {
                    await submitBuyerLoiResubmission(axios, props.engagementId, props.buyerId);
                } else if (props.eventId) {
                    await submitLoiForm(axios, props.engagementId, props.eventId, props.loiFormId);
                }
            } else {
                if (props.isResubmission && props.buyerId != null) {
                    await completeAdminLoiResubmission(axios, props.engagementId, props.buyerId);
                } else if (props.eventId) {
                    await submitAdminLoiForms(axios, props.engagementId, props.eventId, [props.loiFormId]);
                }
            }

            setFormData(prev => prev ? { ...prev, isComplete: true } : null);
            setErrors(null);
            setIsConfirmVisible(false);
            props.onDataChange?.();
            createNotification('Successfully submitted LOI', 'success');
        } catch (error) {
            const message =
                error instanceof Error && error.message
                    ? error.message
                    : 'Could not submit LOI. Please try again.';
            createNotification(message, 'error');
        } finally {
            setIsSubmitting(false);
        }
    }, [axios, props.loiFormId, props.engagementId, props.eventId, props.isResubmission, props.buyerId, formData, handleSave, props.onDataChange, isBuyerLoiFormReview, isBuyerLoiSubmission]);

    const handleApproveOnBehalfClick = useCallback(() => {
        if (!formData) return;

        const validationErrors = validateLoiForm(formData, {
            documentDetailsId: validationDocumentDetailsId,
            isFsb: props.isFsb,
            skipLegalTabs: props.isAdmin,
            isAdmin: props.isAdmin,
        });
        if (hasLoiFormErrors(validationErrors)) {
            setErrors(validationErrors);
            createNotification('Please fill all required fields before approving on behalf of the buyer.', 'error');
            return;
        }

        setErrors(null);
        setIsApproveOnBehalfConfirmVisible(true);
    }, [formData, validationDocumentDetailsId, props.isFsb, props.isAdmin]);

    const handleApproveOnBehalfConfirm = useCallback(async () => {
        if (!axios || !props.engagementId || props.buyerId == null) return;

        setIsSubmitting(true);
        try {
            const saved = await handleSave();
            if (!saved) return;

            await approveLoiOnBehalfOfBuyer(axios, props.engagementId, props.buyerId);

            setFormData(prev => prev ? { ...prev, isComplete: true } : null);
            setErrors(null);
            setIsApproveOnBehalfConfirmVisible(false);
            props.onDataChange?.();
            createNotification('LOI form approved on behalf of the buyer.', 'success');
        } catch (error) {
            const message =
                error instanceof Error && error.message
                    ? error.message
                    : 'Could not approve the LOI form on behalf of the buyer. Please try again.';
            createNotification(message, 'error');
        } finally {
            setIsSubmitting(false);
        }
    }, [axios, props.engagementId, props.buyerId, props.loiFormId, handleSave, props.onDataChange]);

    const getTabClassName = (tabId: LoiSubmissionPackageSubTab) => {
        const parts = ['RRT__tab'];
        if (activeSubTab === tabId) {
            parts.push('RRT__tab--first', 'RRT__tab--selected');
        }
        if (errors) {
            if (tabId === LoiSubmissionPackageSubTab.LoiAndTermsForm) {
                const tabHasError = errors.termFormFields.size > 0 || !!errors.loiFileRequired;
                if (tabHasError) parts.push('error-tab');
            }
            if (!isBuyerLoiSubmission) {
                if (tabId === LoiSubmissionPackageSubTab.LegalTermGuidance && errors.legalIssueIds.size > 0) {
                    parts.push('error-tab');
                }
                if (tabId === LoiSubmissionPackageSubTab.AdditionalNdaCovenants && errors.additionalCovenantIds.size > 0) {
                    parts.push('error-tab');
                }
            }
        }
        return parts.join(' ');
    };

    const handleLoiUploadComplete = useCallback(() => {
        setIsDocumentUploadedLocally(true);
        props.onDataChange?.();
    }, [props.onDataChange]);

    const resolvePendingChange = useCallback((change: PendingFieldChange, approved: boolean) => {
        setFormData(prev => {
            if (!prev) return prev;
            const pendingFieldChanges = (prev.pendingFieldChanges ?? []).filter(c => c.id !== change.id);
            const termForm = approved && prev.termForm
                ? applyPendingChangeToTermForm(prev.termForm, change)
                : prev.termForm;
            return { ...prev, pendingFieldChanges, termForm };
        });
    }, []);

    useEffect(() => {
        if (props.isHistoricalVersion || formDataRef.current?.isComplete) {
            setErrors(null);
            return;
        }
        if (!formData) return;
        setErrors(prev =>
            prev
                ? (isBuyerLoiSubmission
                    ? buildStep1Errors(formData, validationDocumentDetailsId)
                    : validateLoiForm(formData, { documentDetailsId: validationDocumentDetailsId, isFsb: props.isFsb, skipLegalTabs: props.isAdmin, isAdmin: props.isAdmin }))
                : prev
        );
    }, [validationDocumentDetailsId, isDocumentUploadedLocally, props.isFsb, props.isAdmin, props.isHistoricalVersion, isBuyerLoiSubmission, buildStep1Errors, formData]);

    const tabComponents = [
        {
            id: LoiSubmissionPackageSubTab.OwnerObligations,
            content: (
                <LoiAndTermsForm
                    formData={formData!}
                    isReadonly={true}
                    isInitialQuestionsReadonly={true}
                    isQuestionsOnly={true}
                    onChange={() => {}}
                    sellerLocations={formData?.sellerLocations ?? []}
                    hasOwnerEmploymentAgreement={hasOwnerEmploymentAgreement}
                    ownerEmploymentColumns={ownerEmploymentColumns}
                    ownerEmploymentRowDisplayData={ownerEmploymentRowDisplayData}
                    ownerEmploymentTableRows={ownerEmploymentTableRows}
                    hasVendorContracts={hasVendorContracts}
                    vendorContractColumns={vendorContractColumns}
                    vendorContractRowDisplayData={vendorContractRowDisplayData}
                    vendorContractTableRows={vendorContractTableRows}
                    vendorContractProjectId={vendorContractProjectId}
                    vendorContractTableId={vendorContractTableId}
                    vendorContractFileColumnName={vendorContractFileColumnName}
                    vendorContractFileColumnId={vendorContractFileColumnId}
                />
            ),
        },
        {
            id: LoiSubmissionPackageSubTab.LoiAndTermsForm,
            content: (
                <LoiAndTermsForm
                    formData={formData!}
                    isReadonly={isFormReadonly}
                    isInitialQuestionsReadonly={preliminaryQuestionsReadonly}
                    deferAssumptionSave={isAdminRevisionEdit}
                    canEditFairValues={isAdminRevisionEdit || isAdminBuyerReviewEdit}
                    onChange={handleTermFormChange}
                    errors={errors?.termFormFields}
                    opportunityId={props.engagementId}
                    loiFormId={props.loiFormId}
                    uploadedDocumentName={props.uploadedDocumentName}
                    documentDetailsId={documentDetailsId}
                    onUploadComplete={handleLoiUploadComplete}
                    loiFileError={errors?.loiFileRequired}
                    isInitialSubmission={isInitialSubmission || isBuyerAwaitingDetailedForm}
                    isFsb={props.isFsb}
                    isSfas={props.isSfas}
                    isAdmin={props.isAdmin}
                    isBuyer={props.isBuyer}
                    isBuyerReview={isBuyerLoiFormReview}
                    isBuyerReviewConfirmed={effectiveBuyerReviewConfirmed}
                    onFieldCommentChange={handleFieldCommentChange}
                    onAdminCommentChange={handleAdminCommentChange}
                    sellerLocations={formData?.sellerLocations ?? []}
                    hasVendorContracts={hasVendorContracts}
                    vendorContractColumns={vendorContractColumns}
                    vendorContractRowDisplayData={vendorContractRowDisplayData}
                    vendorContractTableRows={vendorContractTableRows}
                    vendorContractProjectId={vendorContractProjectId}
                    vendorContractTableId={vendorContractTableId}
                    vendorContractFileColumnName={vendorContractFileColumnName}
                    vendorContractFileColumnId={vendorContractFileColumnId}
                    hasOwnerEmploymentAgreement={hasOwnerEmploymentAgreement}
                    ownerEmploymentColumns={ownerEmploymentColumns}
                    ownerEmploymentRowDisplayData={ownerEmploymentRowDisplayData}
                    ownerEmploymentTableRows={ownerEmploymentTableRows}
                    isTermsOnly={props.isLoiTabTermsOnly}
                    canApprovePendingChanges={canApprovePendingChanges}
                    onPendingChangeResolved={resolvePendingChange}
                    onDiscardPendingChange={handleDiscardPendingChange}
                />
            ),
        },
        {
            id: LoiSubmissionPackageSubTab.LegalTermGuidance,
            content: (
                <LegalTermGuidance
                    formData={formData!}
                    isReadonly={isLegalTabsReadonly}
                    onChange={handleLegalIssuesChange}
                    invalidItemIds={errors?.legalIssueIds}
                />
            ),
        },
        {
            id: LoiSubmissionPackageSubTab.AdditionalNdaCovenants,
            content: (
                <AdditionalNdaCovenants
                    formData={formData!}
                    isReadonly={isAdditionalNdaReadonly}
                    onChange={handleAdditionalCovenantsChange}
                    invalidItemIds={errors?.additionalCovenantIds}
                />
            ),
        },
    ];

    const visibleSubTabs = props.showOwnerObligationsTab
        ? [
              {
                  id: LoiSubmissionPackageSubTab.OwnerObligations,
                  content: 'Owner Employment & Practice Obligations',
              },
              ...(props.isOwnerObligationsTabOnly
                  ? []
                  : [
                        {
                            id: LoiSubmissionPackageSubTab.LoiAndTermsForm,
                            content: 'LOI Details',
                        },
                        ...LoiSubmissionPackageSubTabs.filter(
                            (item) => item.id !== LoiSubmissionPackageSubTab.LoiAndTermsForm
                        ),
                    ]),
          ]
        : LoiSubmissionPackageSubTabs;

    const renderTabContent = () => {
        const activeTabComponent = tabComponents.find(
            (tab) => tab.id === activeSubTab
        );
        return activeTabComponent?.content ?? null;
    };

    if (isLoading) {
        return (
            <div className="loi-submission-package marketplace-dynamic-container">
                {Helpers.renderTableLoader()}
            </div>
        );
    }

    if (!formData) {
        return (
            <div className="loi-submission-package marketplace-dynamic-container">
                <div className="text-danger">No LOI form data available.</div>
            </div>
        );
    }

    return (
        <div className="loi-submission-package marketplace-dynamic-container">
            <div className="RRT__container">
                <div className="RRT__tabs body-tabs">
                    {visibleSubTabs
                        .filter((item) => !((isInitialSubmission || isBuyerAwaitingDetailedForm) && item.id === LoiSubmissionPackageSubTab.LegalTermGuidance))
                        .map((item) => (
                        <div
                            key={item.id}
                            className={getTabClassName(item.id)}
                            onClick={() => {
                                setActiveSubTab(item.id);
                                props.setSearchParams?.({ subtab: item.id.toString() });
                            }}>
                            {item.content}
                            {props.showAdditionalNdaCovenantsTooltip &&
                                item.id === LoiSubmissionPackageSubTab.AdditionalNdaCovenants && (
                                    <InfoTooltip
                                        text={ADDITIONAL_NDA_COVENANTS_BUYER_TOOLTIP}
                                        idText="loi-additional-nda-covenants-tab"
                                        place="top"
                                    />
                                )}
                        </div>
                    ))}
                </div>
            </div>
            <div onBlur={scheduleAutoSave}>
                {renderTabContent()}
            </div>
            {((!isFormReadonly && !isBuyerLoiFormReview) || isAdminRevisionEdit || isAdminBuyerReviewEdit) && !props.isHistoricalVersion && (
                <div className="loi-submission-package-actions">
                    {isAdminRevisionEdit && (
                        <InfoTooltip
                            text="Saving updates the posted LOI in place — no new version is created."
                            idText="loi-admin-revision-tooltip"
                            place="top"
                        />
                    )}
                    {!isInitialSubmission && (
                        <ButtonLoader
                            buttonText="Save"
                            loaderButtonText="Saving..."
                            disabled={isSaving || isSubmitting}
                            isLoading={isSaving}
                            onClick={handleSave}
                            className="btn btn-primary"
                        />
                    )}
                    {props.canSubmit && (!isInitialSubmission || activeSubTab === LoiSubmissionPackageSubTab.LoiAndTermsForm) && (
                        <>
                            <ButtonLoader
                                buttonText="Submit for Review"
                                loaderButtonText=""
                                disabled={isSubmitting || isSaving}
                                isLoading={false}
                                onClick={handleSubmitClick}
                                className="btn btn-primary"
                            />
                        </>
                    )}
                    {isAdminApproveOnBehalfAvailable && (
                        <ButtonLoader
                            buttonText="Approve on behalf of buyer"
                            loaderButtonText=""
                            disabled={isSubmitting || isSaving}
                            isLoading={false}
                            onClick={handleApproveOnBehalfClick}
                            className="btn btn-primary"
                        />
                    )}
                </div>
            )}
            {isBuyerLoiFormReview && !props.isBuyerReviewConfirmed && !props.isHistoricalVersion && !props.isAdmin && (
                <div className="loi-submission-package-actions">
                    <ButtonLoader
                        buttonText={activeSubTab === LoiSubmissionPackageSubTab.LoiAndTermsForm ? "Save Comments for Submission" : "Save"}
                        loaderButtonText=""
                        disabled={isSaving || isSubmitting}
                        isLoading={false}
                        onClick={handleSave}
                        className="btn btn-primary"
                    />
                    {!props.isLoiCreationEventInProgress && (
                        <ButtonLoader
                            buttonText="Submit LOI for Seller Consideration"
                            loaderButtonText=""
                            disabled={isSubmitting || isSaving}
                            isLoading={false}
                            onClick={handleConfirmLoiClick}
                            className="btn btn-primary"
                        />
                    )}
                </div>
            )}
            <ConfirmationModal
                isVisible={isConfirmVisible}
                setIsVisible={setIsConfirmVisible}
                header={isBuyerLoiSubmission ? 'Submit LOI for Preliminary Review' : isBuyerLoiFormReview ? 'Confirm LOI Review' : 'Submit LOI Detail Form'}
                confirmationText={isBuyerLoiSubmission ? LOI_PRELIMINARY_SUBMIT_CONFIRMATION_TEXT : isBuyerLoiFormReview ? loiReviewSubmitConfirmationText(hasBlankFieldsOnSubmit) : LOI_SUBMIT_CONFIRMATION_TEXT}
                onConfirm={handleSubmitConfirm}
                nextButtonText="Confirm"
                cancelButtonText="Cancel"
                isLoading={isSubmitting}
                closeOnConfirm={false}
            />
            <ConfirmationModal
                isVisible={isApproveOnBehalfConfirmVisible}
                setIsVisible={setIsApproveOnBehalfConfirmVisible}
                header="Approve on Behalf of Buyer"
                confirmationText={LOI_APPROVE_ON_BEHALF_CONFIRMATION_TEXT}
                onConfirm={handleApproveOnBehalfConfirm}
                nextButtonText="Approve"
                cancelButtonText="Cancel"
                isLoading={isSubmitting}
                closeOnConfirm={false}
            />
            <ToastContainer
                containerId={AUTO_SAVE_CONTAINER_ID}
                position="bottom-right"
                transition={Slide}
                className="auto-save-toast-container"
                newestOnTop={false}
            />
        </div>
    );
};
